import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Users, Plus, Search, Baby, Shield, AlertTriangle } from 'lucide-react';
import { useFamily } from '@/hooks/useFamily';
import { useContacts } from '@/hooks/useContacts';
import { ContactCard } from '@/components/contacts/ContactCard';
import { CreateContactForm } from '@/components/contacts/CreateContactForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function MobileContacts() {
  const { family, familyMembers } = useFamily();
  const { contacts, loading, createContact, updateContact, deleteContact } = useContacts(family?.id);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateContact, setShowCreateContact] = useState(false);
  const [contactTypeFilter, setContactTypeFilter] = useState<string>('all');
  const [editingContact, setEditingContact] = useState(null);
  const [deleteContactId, setDeleteContactId] = useState<string | null>(null);

  if (!family) {
    return (
      <div className="p-4">
        <div className="text-center py-8">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Créez d'abord une famille pour gérer vos contacts</p>
        </div>
      </div>
    );
  }

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.organization?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesType = contactTypeFilter === 'all' || 
                       (contactTypeFilter === 'emergency' && contact.is_emergency_contact) ||
                       contact.contact_type === contactTypeFilter;
    
    return matchesSearch && matchesType;
  });

  const handleCall = (phone: string) => {
    window.open(`tel:${phone}`, '_self');
  };

  const handleEmail = (email: string) => {
    window.open(`mailto:${email}`, '_self');
  };

  const handleCreateContact = async (contactData: any) => {
    const success = await createContact(contactData);
    if (success) {
      setShowCreateContact(false);
    }
  };

  const handleEditContact = async (contactData: any) => {
    if (!editingContact) return;
    
    const success = await updateContact(editingContact.id, contactData);
    if (success) {
      setEditingContact(null);
    }
  };

  const handleDeleteContact = async () => {
    if (!deleteContactId) return;
    
    const success = await deleteContact(deleteContactId);
    if (success) {
      setDeleteContactId(null);
    }
  };

  const emergencyContacts = contacts.filter(contact => contact.is_emergency_contact);
  const adults = familyMembers.filter(member => member.profile_id);
  const children = familyMembers.filter(member => !member.profile_id);

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Contacts</h1>
          <p className="text-sm text-muted-foreground">
            {filteredContacts.length} contact{filteredContacts.length > 1 ? 's' : ''}
            {emergencyContacts.length > 0 && ` • ${emergencyContacts.length} urgence${emergencyContacts.length > 1 ? 's' : ''}`}
          </p>
        </div>
        <Dialog open={showCreateContact} onOpenChange={setShowCreateContact}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nouveau contact</DialogTitle>
            </DialogHeader>
            <CreateContactForm
              familyMembers={familyMembers}
              onSubmit={handleCreateContact}
              onCancel={() => setShowCreateContact(false)}
              loading={loading}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and filters */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Rechercher un contact..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={contactTypeFilter} onValueChange={setContactTypeFilter}>
          <SelectTrigger>
            <SelectValue placeholder="Filtrer par type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les contacts ({contacts.length})</SelectItem>
            <SelectItem value="emergency">
              <div className="flex items-center gap-2">
                <Shield className="h-3 w-3 text-red-500" />
                Contacts d'urgence ({emergencyContacts.length})
              </div>
            </SelectItem>
            <SelectItem value="doctor">Médecins</SelectItem>
            <SelectItem value="school">École</SelectItem>
            <SelectItem value="babysitter">Baby-sitters</SelectItem>
            <SelectItem value="family_friend">Proches</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Emergency contacts section */}
      {emergencyContacts.length > 0 && contactTypeFilter === 'all' && (
        <Card className="shadow-soft border-red-200 bg-red-50/50">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-red-700">
              <Shield className="h-4 w-4" />
              Contacts d'urgence
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {emergencyContacts.slice(0, 2).map((contact) => (
              <div key={contact.id} className="flex items-center gap-3 p-2 rounded-lg bg-white">
                <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">
                    {contact.first_name} {contact.last_name}
                  </p>
                  {contact.phone_primary && (
                    <p className="text-xs text-muted-foreground">{contact.phone_primary}</p>
                  )}
                </div>
                {contact.phone_primary && (
                  <Button 
                    size="sm" 
                    variant="destructive"
                    onClick={() => handleCall(contact.phone_primary!)}
                  >
                    Appeler
                  </Button>
                )}
              </div>
            ))}
            {emergencyContacts.length > 2 && (
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={() => setContactTypeFilter('emergency')}
              >
                Voir tous les contacts d'urgence ({emergencyContacts.length})
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Contacts list */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredContacts.length === 0 ? (
        <div className="text-center py-8">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">
            {searchQuery || contactTypeFilter !== 'all' ? 'Aucun contact trouvé' : 'Aucun contact pour le moment'}
          </p>
          {!searchQuery && contactTypeFilter === 'all' && (
            <Button onClick={() => setShowCreateContact(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Créer le premier contact
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredContacts.map((contact) => (
            <ContactCard
              key={contact.id}
              contact={contact}
              onEdit={() => setEditingContact(contact)}
              onDelete={() => setDeleteContactId(contact.id)}
              onCall={handleCall}
              onEmail={handleEmail}
            />
          ))}
        </div>
      )}

      {/* Edit Contact Dialog */}
      <Dialog open={!!editingContact} onOpenChange={(open) => !open && setEditingContact(null)}>
        <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le contact</DialogTitle>
          </DialogHeader>
          {editingContact && (
            <CreateContactForm
              familyMembers={familyMembers}
              initialData={editingContact}
              onSubmit={handleEditContact}
              onCancel={() => setEditingContact(null)}
              loading={loading}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteContactId} onOpenChange={(open) => !open && setDeleteContactId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le contact</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer ce contact ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteContact} className="bg-destructive text-destructive-foreground">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}